/**
 * 极简 Markdown → HTML + HTML 净化。文章区块用于渲染字段里的富文本
 * （常为 AI 生成的报告/说明）。不引第三方库；净化只保留安全标签，去掉
 * script/style/事件属性/危险协议——成品在无 allow-scripts 的 iframe 里，
 * 已双保险。
 */

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** 极简 Markdown：标题/粗斜体/行内代码/链接/无序有序列表/引用/分隔线/段落。 */
export function markdownToHtml(md: string): string {
  const lines = String(md ?? '').replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (listType) { out.push(`</${listType}>`); listType = null; }
  };
  const inline = (t: string) =>
    escapeHtml(t)
      .replace(/`([^`]+)`/g, '<code style="background:#f2f2f2;padding:0 3px;border-radius:3px;">$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
      .replace(/\*([^*]+)\*/g, '<i>$1</i>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') { closeList(); continue; }
    let m: RegExpMatchArray | null;
    if ((m = line.match(/^(#{1,6})\s+(.*)$/))) {
      closeList();
      const lvl = m[1].length;
      const size = [26, 22, 19, 17, 15, 14][lvl - 1];
      out.push(`<div style="font-weight:700;font-size:${size}px;margin:10px 0 6px;">${inline(m[2])}</div>`);
    } else if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      closeList();
      out.push('<hr style="border:none;border-top:1px solid #ccc;margin:10px 0;" />');
    } else if ((m = line.match(/^>\s?(.*)$/))) {
      closeList();
      out.push(`<blockquote style="border-left:3px solid #ddd;padding-left:10px;color:#555;margin:6px 0;">${inline(m[1])}</blockquote>`);
    } else if ((m = line.match(/^[-*]\s+(.*)$/))) {
      if (listType !== 'ul') { closeList(); out.push('<ul style="margin:4px 0 4px 20px;">'); listType = 'ul'; }
      out.push(`<li>${inline(m[1])}</li>`);
    } else if ((m = line.match(/^\d+\.\s+(.*)$/))) {
      if (listType !== 'ol') { closeList(); out.push('<ol style="margin:4px 0 4px 20px;">'); listType = 'ol'; }
      out.push(`<li>${inline(m[1])}</li>`);
    } else {
      closeList();
      out.push(`<p style="margin:4px 0;">${inline(line)}</p>`);
    }
  }
  closeList();
  return out.join('\n');
}

const ALLOWED = new Set([
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'blockquote', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'span', 'div',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img',
]);

/** 自闭合 / 无需闭合的标签，不参与配对。 */
const VOID_TAGS = new Set(['br', 'hr', 'img']);

/**
 * style 里放行的属性。放开整个 style 等于把排版控制权交给外部内容：
 * `position:fixed`、`display:none`、`page-break-after:always`、`font-size:900px`
 * 都能穿过来，把整页版式搞坏。
 */
const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color', 'font-weight', 'font-style', 'font-size',
  'text-align', 'text-decoration', 'line-height', 'margin', 'padding',
  'border', 'border-collapse', 'vertical-align', 'white-space',
]);

function filterStyle(value: string): string {
  return value
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => {
      const name = decl.split(':')[0]?.trim().toLowerCase();
      if (!name || !SAFE_STYLE_PROPS.has(name)) return false;
      // font-size 限个上限，免得一行字顶掉整页
      if (name === 'font-size') {
        const px = /([\d.]+)\s*px/.exec(decl);
        if (px && Number(px[1]) > 48) return false;
      }
      return true;
    })
    .join('; ');
}

/**
 * 补齐未闭合的标签。
 *
 * 文章区块的内容来自用户表里的富文本，AI 生成或复制粘贴时经常是截断的。
 * 一个没闭合的 `<table>` 或 `<div>` 会把它后面的所有区块（签章、页脚）
 * 全都吞进自己里面，客户看到的是「后半张单据没了」。
 */
function balanceTags(html: string): string {
  const stack: string[] = [];
  const out = html.replace(/<(\/?)([a-zA-Z0-9]+)([^>]*)>/g, (full, slash: string, tag: string) => {
    const name = tag.toLowerCase();
    if (VOID_TAGS.has(name)) return full;
    if (!slash) {
      stack.push(name);
      return full;
    }
    const idx = stack.lastIndexOf(name);
    if (idx === -1) return ''; // 没有对应开标签的闭标签，丢掉
    stack.length = idx; // 顺带关掉中间未闭合的
    return full;
  });
  const tail = stack
    .reverse()
    .map((name) => `</${name}>`)
    .join('');
  return out + tail;
}

/** 净化 HTML：白名单标签，去事件属性/危险协议/script/style 内容。 */
export function sanitizeHtml(html: string): string {
  // 移除 script/style 整块
  let s = String(html ?? '').replace(/<(script|style)[\s\S]*?<\/\1>/gi, '');
  // 逐标签过滤
  s = s.replace(/<\/?([a-zA-Z0-9]+)([^>]*)>/g, (full, tag: string, attrs: string) => {
    const name = tag.toLowerCase();
    if (!ALLOWED.has(name)) return '';
    if (full.startsWith('</')) return `</${name}>`;
    // 只保留安全属性
    const safe: string[] = [];
    const attrRe = /([a-zA-Z-]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
    let a: RegExpExecArray | null;
    while ((a = attrRe.exec(attrs))) {
      const k = a[1].toLowerCase();
      const v = a[3] ?? a[4] ?? '';
      if (k.startsWith('on')) continue;
      if ((k === 'href' || k === 'src') && /^\s*(javascript|data):/i.test(v) && !/^data:image\//i.test(v)) continue;
      if (['href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'style', 'width', 'height'].includes(k)) {
        if (k === 'style') {
          // 只放行版式安全的属性（见 SAFE_STYLE_PROPS）
          const filtered = filterStyle(v);
          if (!filtered) continue;
          safe.push(`style="${filtered.replace(/"/g, '&quot;')}"`);
          continue;
        }
        // colspan/rowspan/width/height 给个上限，免得一个 colspan="100" 把表撑爆
        if (['colspan', 'rowspan'].includes(k) && Number(v) > 30) continue;
        if (['width', 'height'].includes(k) && Number(v) > 2000) continue;
        safe.push(`${k}="${v.replace(/"/g, '&quot;')}"`);
      }
    }
    return `<${name}${safe.length ? ' ' + safe.join(' ') : ''}>`;
  });
  // 最后补齐未闭合的标签，别让残缺内容吃掉后面的区块
  return balanceTags(s);
}

export function renderRichTextValue(value: string, format: 'markdown' | 'html'): string {
  if (!value) return '';
  return format === 'markdown' ? markdownToHtml(value) : sanitizeHtml(value);
}
